import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from '@shared/store/dialog.duck';
import {
  confirmDeskForReservation,
  goBackToPreviousState,
  selectHideFavouriteControls,
  selectOfficeForReservation,
  selectSelectedArea,
  selectSelectedDesk,
  selectSelectedFloor,
  selectSelectionOffice
} from '@features/desks/store/deskReservation.duck';
import { MobileOnly } from "@shared/ui/atoms/DeviceSpecific";
import { GoBackButton } from "@shared/ui/atoms/buttons/GoBackButton";
import DialogHeader from "@shared/dialogs/DialogHeader";
import { BodyLarge, BodyRegular, BodySmall } from "@shared/ui/atoms/fonts/Body";
import styled from "styled-components";
import { Column, Row } from "@shared/ui/atoms/StructuralLayout";
import Colours from "@shared/ui/atoms/Colours";
import { FavouriteImage } from "@shared/ui/atoms/FavouriteImage";
import {
  selectFavouriteDesks,
  selectFavouriteDesksLoading,
  toggleDefaultOffice,
  toggleFavoriteDesk
} from '@features/desks/store/advanceHotDeskingSetup.duck';
import { WithFavouriteOffices } from '@features/desks/hooks/AdvanceHotDeskingHooks';
import { OfficeEntityWithFavourite } from '@features/desks/services/AdvanceHotDeskingService';
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import { SimpleSpinner } from "@shared/ui/atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { DeskPickerContentCard } from "./DeskPickerContentCard";
import { SelectedParkingContentCard } from "./SelectedParkingContentCard";
import { selectActiveDay, selectUserRequiredParking } from '@features/whereabouts/store/editMovements.duck';
import { Switch } from "@shared/ui/atoms/Switch";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

interface Props {
  showCarParkingOption?: boolean;
}

export function LocationSelectionDone(props: Props) {
  const dispatch = useDispatch();
  const close = () => dispatch(closeDialog());
  const {t} = useTranslation();
  const [showSelectedParkingContent, setShowSelectedParkingContent] = useState(false);

  const isParkingSelected = useSelector(selectUserRequiredParking);
  const selectedOffice = useSelector(selectSelectionOffice);
  const selectedFloor = useSelector(selectSelectedFloor);
  const selectedArea = useSelector(selectSelectedArea);
  const allocatedDesk = useSelector(selectSelectedDesk);
  const hideFavouriteButton = useSelector(selectHideFavouriteControls);

  const favoritedOffices = WithFavouriteOffices();
  const favoritedDesks = useSelector(selectFavouriteDesks);
  const favoritingDesksLoading = useSelector(selectFavouriteDesksLoading);
  const date = useSelector(selectActiveDay);
  const showCarParkingOption = !!props?.showCarParkingOption;

  const isFavorited = useMemo(() => {
    if (allocatedDesk) {
      return !!favoritedDesks.find(fd => fd.deskId === allocatedDesk.id);
    } else {
      const matchingArray = [selectedOffice?.id, selectedFloor?.id, selectedArea?.id].filter(a => !!a);
      return favoritedOffices.filter((fo: OfficeEntityWithFavourite) => matchingArray.includes(fo.id)).length > 0
    }
  }, [favoritedOffices, selectedOffice, selectedFloor, selectedArea, allocatedDesk, favoritedDesks]);

  const makeFavourite = (e: Event) => {
    e.stopPropagation();
    if (allocatedDesk) {
      dispatch(toggleFavoriteDesk(allocatedDesk?.id))
    } else if (selectedArea) {
      dispatch(toggleDefaultOffice(selectedArea?.id))
    } else if (selectedFloor) {
      dispatch(toggleDefaultOffice(selectedFloor?.id))
    } else {
      dispatch(toggleDefaultOffice(selectedOffice?.id))
    }
  };

  const save = () => {
    if (allocatedDesk) {
      dispatch(confirmDeskForReservation(allocatedDesk));
    } else if (selectedArea) {
      dispatch(selectOfficeForReservation(selectedArea));
    } else if (selectedFloor) {
      dispatch(selectOfficeForReservation(selectedFloor));
    } else if (selectedOffice) {
      dispatch(selectOfficeForReservation(selectedOffice));
    }
  }

  const goBackToPreviousView = () => {
    dispatch(goBackToPreviousState());
  }

  return (
    <>
      <MobileOnly>
        <GoBackButton onClick={close} text="back-to-whereabouts"/>
      </MobileOnly>
      <DialogHeader title="desk-booking.confirm-booking"/>
      {date && <BodyLarge weight={600} style={{marginBottom: 16}}>{date.format('dddd DD MMMM')}</BodyLarge>}

      <DeskPickerContentCard style={{marginBottom: 8}}>
        <Row style={{marginBottom: 12}}>
          <Column style={{width: '100%'}}>
            <BodyRegular weight={600}>{selectedOffice?.label}</BodyRegular>
            <BodySmall>{selectedFloor?.label}{!!selectedArea ? `: ${selectedArea.label}` : ''}</BodySmall>
            {allocatedDesk && <BodySmall>{allocatedDesk?.label}</BodySmall>}
          </Column>
        </Row>
        {!hideFavouriteButton && <Row style={{alignSelf: 'center', cursor: 'pointer'}}
                                      onClick={(e: any) => makeFavourite(e)}
                                      data-test={'add-to-favourites'}>

          <IconWrapper>
            {favoritingDesksLoading ? <SimpleSpinner size={24} /> : <FavouriteImage src={isFavorited ? '/assets/icons/StarActive.svg' : '/assets/icons/Star.svg'}
                                                                                    alt="Favourite"
                                                                                    style={{marginRight: 12}}/>}
          </IconWrapper>

          <BodyRegular colour={Colours.blue}
                       weight={600}>{t(isFavorited ? 'added-to-favourites' : 'add-to-your-favourites')}</BodyRegular>
        </Row>}
      </DeskPickerContentCard>

      {showCarParkingOption && <>
        <Switch value={showSelectedParkingContent} style={{marginTop: 16, marginBottom: 8}}
                label={'Book parking space'}
                onChange={setShowSelectedParkingContent} />
      </>}

      {(isParkingSelected || showSelectedParkingContent) && <SelectedParkingContentCard overrideIsParkingSelected={showSelectedParkingContent} />}

      <PrimaryButton click={save}
                     text="button.confirm"
                     size={ButtonSize.Large}
                     style={{marginBottom: 16, marginTop: 48}}
                     fullWidth={true}
                     dataTest={'confirm-desk-reservation-button'}/>

      <OutlineButton click={goBackToPreviousView}
                     text="button.back-and-change"
                     size={ButtonSize.Large}
                     fullWidth={true} />
    </>
  )
}

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
`
